import { GetStaticProps } from 'next';

import ApiService from '@/lib/api';
import { CaseStudyCard, CaseStudyTaxonomies } from '@/types/pages';
import { CaseStudyList } from '@/components/CaseStudies';
import CaseStudyCta from '@/components/CaseStudyCta';
import Fold from '@/components/Fold';
import { imageUrlBuilder } from '@/utils/helpers';
import MainLayout, { OG_IMAGE_HEIGHT, OG_IMAGE_WIDTH } from '../../layouts/MainLayout';
import React from 'react';
import { SEO } from '@/types/general';
import { withSiteName } from '@/utils/pageTitle';

interface PageData {
  heading?: string;
  subheading?: string;
  bgImage?: Record<string, unknown>;
  bgImageMobile?: Record<string, unknown>;
  bgColor?: { hex: string };
  textColor?: { hex: string };
  ctaTitle?: string;
  ctaButtonTitle?: string;
  ctaAction?: string;
  ctaBgColor?: { hex: string };
  ctaTextColor?: { hex: string };
  seo?: SEO;
}

interface CaseStudiesIndexProps {
  pageData: PageData | null;
  initialCards: CaseStudyCard[];
  taxonomies: CaseStudyTaxonomies;
  menuData: unknown;
  footer: unknown;
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
  const api = new ApiService();
  // The card list stays published-only in preview: a draft and its published
  // version would both match and show up twice in the grid.
  const [initialCards, taxonomies, pageData, menuData, miscData] = await Promise.all([
    api.getAllCaseStudiesCards(),
    api.getCaseStudyTaxonomies(),
    api.getCaseStudiesPageData(preview),
    api.getMenuData(),
    api.getMiscData(),
  ]);
  const { footer } = miscData;

  return {
    props: {
      pageData: pageData ?? null,
      initialCards,
      taxonomies,
      menuData,
      footer,
    },
    revalidate: 3600,
  };
};

const CaseStudiesPage: React.FC<CaseStudiesIndexProps> = ({ pageData, initialCards, taxonomies, menuData, footer }) => {
  const heading = pageData?.heading ?? "What we've built and how we've built it";
  const subheading =
    pageData?.subheading ??
    "Real products, real partners, real results. Here's how we move purposely with the teams we work alongside.";

  // Hero sits over a photographic banner: default to white for contrast when no textColor is set
  const textColor = pageData?.textColor ?? { hex: '#ffffff' };

  // The hero banner doubles as the share card. Filtered views (?industry=…) intentionally
  // reuse it: MainLayout strips the query string when building the canonical, so every
  // filtered URL already canonicalizes to /case-studies and shares its preview.
  const bgImageAsset = (pageData?.bgImage as { asset?: unknown } | undefined)?.asset;
  const ogImage = bgImageAsset
    ? imageUrlBuilder
        .image(bgImageAsset)
        .width(OG_IMAGE_WIDTH)
        .height(OG_IMAGE_HEIGHT)
        .fit('crop')
        .auto('format')
        .quality(80)
        .url()
    : undefined;

  return (
    <MainLayout
      navIsFixed={true}
      navIsDark={false}
      bgWhite={true}
      ogUrl="https://foxbox.com/case-studies"
      ogImage={ogImage}
      ogImageWidth={ogImage ? OG_IMAGE_WIDTH : undefined}
      ogImageHeight={ogImage ? OG_IMAGE_HEIGHT : undefined}
      metaTitle={pageData?.seo?.title || withSiteName('Case Studies')}
      metaDescription={pageData?.seo?.description || subheading}
      menuData={menuData}
      footer={footer}
    >
      <Fold
        heading={heading}
        subHeading={subheading}
        bgImage={pageData?.bgImage as Record<string, string> | undefined}
        bgColor={pageData?.bgColor}
        textColor={textColor}
      />

      <CaseStudyList initialCards={initialCards} taxonomies={taxonomies} showFilters />

      <CaseStudyCta
        title={pageData?.ctaTitle}
        buttonTitle={pageData?.ctaButtonTitle}
        buttonAction={pageData?.ctaAction}
        buttonBgColor={pageData?.ctaBgColor}
        buttonTextColor={pageData?.ctaTextColor}
      />
    </MainLayout>
  );
};

export default CaseStudiesPage;
